import { useEffect, useRef } from "react";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";

// Categorical bar chart (uPlot) for harmonic / interharmonic spectra.
//
// x is an integer category axis (harmonic order), NOT time — so no time
// scale, no zoom plugin. Optionally overlays one line series (used to draw
// interharmonic subgroups over interharmonic-group bars). Imperative uPlot
// lifecycle, same shape as WaveformChart: one instance, ResizeObserver,
// destroy on unmount. Rebuilds when the data arrays change identity — the
// analysis is fetched once, so that happens only on channel switch.

export interface BarSeries {
  label: string;
  values: number[];
  color: string;
}

export interface BarChartUplotProps {
  x: number[];
  bars: BarSeries;
  line?: BarSeries;
  height?: number;
  xLabel?: string;
  yLabel?: string;
}

export function BarChartUplot({
  x,
  bars,
  line,
  height = 220,
  xLabel,
  yLabel,
}: BarChartUplotProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const uRef = useRef<uPlot | null>(null);

  useEffect(() => {
    if (!hostRef.current) return;

    const series: uPlot.Series[] = [
      {},
      {
        label: bars.label,
        stroke: bars.color,
        fill: bars.color,
        paths: uPlot.paths.bars!({ size: [0.62, 22] }),
        points: { show: false },
      },
    ];
    const data: uPlot.AlignedData = [x, bars.values];
    if (line) {
      series.push({
        label: line.label,
        stroke: line.color,
        width: 2,
        points: { show: false },
      });
      data.push(line.values);
    }

    const opts: uPlot.Options = {
      width: hostRef.current.clientWidth,
      height,
      scales: { x: { time: false }, y: { auto: true } },
      series,
      axes: [
        {
          stroke: "#94a3b8",
          grid: { show: false },
          label: xLabel,
          labelSize: xLabel ? 22 : undefined,
          values: (_u, ticks) => ticks.map((t) => String(t)),
        },
        {
          stroke: "#94a3b8",
          grid: { stroke: "rgba(148,163,184,0.12)" },
          label: yLabel,
          labelSize: yLabel ? 30 : undefined,
        },
      ],
      legend: { show: !!line },
      cursor: { drag: { x: false, y: false } },
    };

    const u = new uPlot(opts, data, hostRef.current);
    uRef.current = u;

    const ro = new ResizeObserver((entries) =>
      uRef.current?.setSize({
        width: entries[0].contentRect.width,
        height,
      })
    );
    ro.observe(hostRef.current);

    return () => {
      ro.disconnect();
      u.destroy();
      uRef.current = null;
    };
  }, [x, bars, line, height, xLabel, yLabel]);

  return <div ref={hostRef} style={{ width: "100%", height }} />;
}
